import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm, usePrompt } from "@/features/confirm/ConfirmProvider";
import { formatDate, formatMoney } from "@bookboost/shared/format";

// ---------------------------------------------------------------------------
// Types mirrored from apps/worker/src/lib/workbench.ts (WorkbenchQueue et al).
// ---------------------------------------------------------------------------

type WorkbenchState = "ready" | "needs_review" | "needs_link" | "blocked" | "no_invoice";
type WorkbenchBlocker = "no_rep_mapped" | "no_structure" | "no_amount";

interface WorkbenchDeal {
  id: string;
  deal_name: string | null;
  hubspot_deal_id: string;
  hubspot_owner_id: string | null;
  owner_id: string | null;
  amount: number | null;
  currency: string | null;
  closed_won_at: string | null;
  company_name: string | null;
}

interface WorkbenchInvoice {
  id: string;
  stripe_invoice_id: string;
  paid_at: string | null;
  amount: number;
  currency: string;
}

interface WorkbenchLineItem {
  description: string | null;
  amount: number | null;
  product: string | null;
}

interface WorkbenchSuggestion {
  stripeCustomerId: string;
  name: string | null;
  email: string | null;
  rank: number;
  reason: string;
}

interface WorkbenchRow {
  deal: WorkbenchDeal;
  state: WorkbenchState;
  blocker?: WorkbenchBlocker;
  invoice?: WorkbenchInvoice;
  lineItems?: WorkbenchLineItem[];
  suggestion?: WorkbenchSuggestion;
}

interface WorkbenchQueue {
  rows: WorkbenchRow[];
  counts: Record<WorkbenchState, number>;
  dismissed: Array<{ deal: WorkbenchDeal; reason: string | null; at: string; by: string | null }>;
}

interface RepUser {
  id: string;
  email: string;
  full_name: string | null;
  role: "admin" | "rep" | "finance";
  active: boolean;
}

type Strategy = "metadata" | "contact_email" | "domain" | "company_name" | "deal_name";

interface ReconcileResult {
  ok: boolean;
  scanned: number;
  linked: number;
  commissionsCreated: number;
  noMatch: number;
  ambiguous: Array<{
    invoiceId: string;
    stripeInvoiceId: string;
    candidates: Array<{ dealId: string; dealName: string | null; strategy: Strategy }>;
    customerName: string | null;
    customerEmail: string | null;
  }>;
  errors: string[];
}

const STATE_ORDER: WorkbenchState[] = ["ready", "needs_review", "needs_link", "blocked", "no_invoice"];

const STATE_META: Record<WorkbenchState, { chip: string; title: string; blurb: string; badge: string }> = {
  ready: {
    chip: "ready",
    title: "Ready — create the commission",
    blurb: "A linked, paid, commissionable invoice exists. One click fires the (idempotent) engine.",
    badge: "badge earned",
  },
  needs_review: {
    chip: "need review",
    title: "Needs review — classify the invoice's products",
    blurb:
      "A paid invoice exists but its products have no commissionable rule. Setting a rule recomputes every invoice, so the deal reclassifies on the spot.",
    badge: "badge pending",
  },
  needs_link: {
    chip: "need link",
    title: "Needs link — suggested customer match",
    blurb:
      "An unlinked Stripe customer with paid commissionable invoices looks like this deal. Linking cascades onto its invoices and creates the commission.",
    badge: "badge approved",
  },
  blocked: {
    chip: "blocked",
    title: "Blocked — fix the rep, structure, or amount",
    blurb: "The trigger can't fire until the blocker is cleared. Fix inline where possible.",
    badge: "badge",
  },
  no_invoice: {
    chip: "no invoice yet",
    title: "No invoice yet",
    blurb: "Nothing paid, nothing suggested. These usually resolve themselves when Stripe invoices land.",
    badge: "badge",
  },
};

const BLOCKER_LABEL: Record<WorkbenchBlocker, string> = {
  no_rep_mapped: "no rep mapped",
  no_structure: "no commission structure",
  no_amount: "no deal amount",
};

const QUEUE_KEY = ["admin", "reconcile", "queue"] as const;

export const Route = createFileRoute("/admin/reconcile")({
  component: ReconcilePage,
});

function ReconcilePage() {
  const toast = useToast();
  const confirm = useConfirm();
  const promptInput = usePrompt();
  const qc = useQueryClient();

  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  const [repChoice, setRepChoice] = useState<Record<string, string>>({});
  const [showNoInvoice, setShowNoInvoice] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [showBulkAssist, setShowBulkAssist] = useState(false);
  const [lastRun, setLastRun] = useState<ReconcileResult | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () => api.get<WorkbenchQueue>("/api/reconcile/queue"),
  });

  const rows = data?.rows ?? [];
  const grouped = useMemo(() => {
    const g: Record<WorkbenchState, WorkbenchRow[]> = {
      ready: [],
      needs_review: [],
      needs_link: [],
      blocked: [],
      no_invoice: [],
    };
    for (const r of rows) g[r.state].push(r);
    return g;
  }, [rows]);

  const hasUnmappedRep = grouped.blocked.some((r) => r.blocker === "no_rep_mapped");
  const repsQuery = useQuery({
    queryKey: ["admin", "reps", "workbench"],
    queryFn: () => api.get<{ users: RepUser[] }>("/api/admin/reps"),
    enabled: hasUnmappedRep,
  });
  const repOptions = (repsQuery.data?.users ?? []).filter((u) => u.active);

  function invalidateQueue() {
    qc.invalidateQueries({ queryKey: QUEUE_KEY });
    qc.invalidateQueries({ queryKey: ["admin", "overview"] });
  }

  // -- Mutations -------------------------------------------------------------

  const trigger = useMutation({
    mutationFn: ({ dealId }: { dealId: string; dealName: string }) =>
      api.post<{ ok: true; kind: "created" | "skipped"; commissionId?: string; reason?: string }>(
        `/api/admin/deals/${encodeURIComponent(dealId)}/trigger-commission`,
      ),
    onSuccess: (res, vars) => {
      if (res.kind === "skipped") {
        toast.warn(`${vars.dealName}: a commission already existed — nothing created.`);
      } else {
        toast.ok(`Commission created for ${vars.dealName}.`);
      }
      invalidateQueue();
      qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? `Couldn't create commission: ${err.message}` : "Couldn't create commission."),
  });

  async function onTrigger(row: WorkbenchRow) {
    const name = row.deal.deal_name ?? row.deal.hubspot_deal_id;
    const inv = row.invoice;
    const ok = await confirm({
      title: "Create commission",
      prompt: `Create the commission for “${name}” from invoice ${inv?.stripe_invoice_id ?? "—"} (${
        inv ? formatMoney(inv.amount, inv.currency) : "—"
      }, paid ${formatDate(inv?.paid_at)}). The commission basis is the deal amount (${formatMoney(
        row.deal.amount,
        row.deal.currency,
      )}), not the invoice. Idempotent — safe if it already exists.`,
      confirmLabel: "Create commission",
    });
    if (ok) trigger.mutate({ dealId: row.deal.id, dealName: name });
  }

  const dismiss = useMutation({
    mutationFn: ({ dealId, reason }: { dealId: string; reason: string; dealName: string }) =>
      api.post(`/api/admin/deals/${encodeURIComponent(dealId)}/exempt`, { reason }),
    onSuccess: (_res, vars) => {
      toast.ok(`Dismissed ${vars.dealName}. It won't count against the queue (restorable below).`);
      invalidateQueue();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't dismiss."),
  });

  async function onDismiss(row: WorkbenchRow) {
    const name = row.deal.deal_name ?? row.deal.hubspot_deal_id;
    const reason = await promptInput({
      title: "Dismiss deal",
      prompt: `“${name}” will leave the queue as “no commission expected”. The reason is audit-logged and shown in the dismissed list.`,
      label: "Reason",
      placeholder: "e.g. founder-sourced, no rep involved",
      confirmLabel: "Dismiss",
      multiline: true,
    });
    if (reason == null) return;
    if (reason.trim().length < 3) {
      toast.err("Reason must be at least 3 characters.");
      return;
    }
    dismiss.mutate({ dealId: row.deal.id, reason: reason.trim(), dealName: name });
  }

  const restore = useMutation({
    mutationFn: ({ dealId }: { dealId: string; dealName: string }) =>
      api.post(`/api/admin/deals/${encodeURIComponent(dealId)}/unexempt`),
    onSuccess: (_res, vars) => {
      toast.ok(`Restored ${vars.dealName} to the queue.`);
      invalidateQueue();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't restore."),
  });

  const mapOwner = useMutation({
    mutationFn: ({ hubspotOwnerId, userId }: { hubspotOwnerId: string; userId: string }) =>
      api.post<{ ok: true; dealsBackfilled: number }>("/api/admin/reps/map-owner", {
        hubspot_owner_id: hubspotOwnerId,
        user_id: userId,
      }),
    onSuccess: (res) => {
      toast.ok(`Rep mapped. ${res.dealsBackfilled} deal${res.dealsBackfilled === 1 ? "" : "s"} backfilled.`);
      invalidateQueue();
      qc.invalidateQueries({ queryKey: ["admin", "reps"] });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.message === "owner_already_mapped_to_another_user"
          ? "That HubSpot owner is already mapped to a different user."
          : err instanceof ApiError
            ? err.message
            : "Couldn't map rep.";
      toast.err(msg);
    },
  });

  const linkAndCreate = useMutation({
    mutationFn: ({ stripeCustomerId, hubspotDealId }: { stripeCustomerId: string; hubspotDealId: string; dealName: string }) =>
      api.post(`/api/admin/customers/${encodeURIComponent(stripeCustomerId)}/link`, {
        hubspot_deal_id: hubspotDealId,
      }),
    onSuccess: (_res, vars) => {
      toast.ok(`Linked customer to ${vars.dealName}. Paid commissionable invoices trigger the commission automatically.`);
      invalidateQueue();
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? `Link failed: ${err.message}` : "Link failed."),
  });

  async function onLinkAndCreate(row: WorkbenchRow) {
    const s = row.suggestion;
    if (!s) return;
    const name = row.deal.deal_name ?? row.deal.hubspot_deal_id;
    const ok = await confirm({
      title: "Link & create",
      prompt: `Link Stripe customer “${s.name ?? s.email ?? s.stripeCustomerId}” to “${name}” (matched via ${s.reason}). Linking cascades the deal onto the customer's invoices; already-paid commissionable invoices create the commission immediately.`,
      confirmLabel: "Link & create",
    });
    if (ok)
      linkAndCreate.mutate({
        stripeCustomerId: s.stripeCustomerId,
        hubspotDealId: row.deal.hubspot_deal_id,
        dealName: name,
      });
  }

  // Product classification. The rule PUT alone only stores the rule — invoice
  // `commissionable` is precomputed — so we chain the existing recompute
  // endpoint to actually reclassify invoices (and therefore this queue).
  const classify = useMutation({
    mutationFn: async ({ productId, action }: { productId: string; action: "commissionable" | "excluded" | "onboarding" }) => {
      if (action === "onboarding") {
        await api.put(`/api/admin/products/${encodeURIComponent(productId)}/onboarding`, { is_onboarding: true });
      } else {
        await api.put(`/api/admin/products/${encodeURIComponent(productId)}/rule`, {
          commissionable: action === "commissionable",
        });
      }
      return api.post<{ scanned: number; flipped: number }>("/api/admin/products/recompute");
    },
    onSuccess: (res, vars) => {
      const what =
        vars.action === "commissionable" ? "commissionable" : vars.action === "excluded" ? "not commissionable" : "onboarding";
      toast.ok(`Marked ${vars.productId} as ${what}. Recomputed ${res.scanned} invoices (${res.flipped} flipped).`, 6000);
      invalidateQueue();
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't set the product rule."),
  });

  // Legacy auto-matcher, kept as bulk assist.
  const autoLink = useMutation({
    mutationFn: ({ dry }: { dry: boolean }) =>
      api.post<ReconcileResult>("/api/reconcile/auto-link", undefined, { searchParams: { dry: dry ? "1" : "" } }),
    onSuccess: (res, vars) => {
      setLastRun(res);
      toast.ok(
        `Scanned ${res.scanned} invoices. Linked ${res.linked}, ambiguous ${res.ambiguous.length}, no-match ${res.noMatch}, commissions created ${res.commissionsCreated}.`,
        9000,
      );
      if (!vars.dry && res.linked > 0) {
        invalidateQueue();
        qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
        qc.invalidateQueries({ queryKey: ["admin", "customers"] });
        qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
      }
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Reconcile failed."),
  });

  async function onAutoLinkLive() {
    const ok = await confirm({
      title: "Run auto-matcher",
      prompt:
        "Auto-link every unambiguous match between unlinked Stripe invoices and HubSpot deals. Newly linked paid invoices may produce commissions. Ambiguous matches are not auto-applied.",
      confirmLabel: "Auto-link",
    });
    if (ok) autoLink.mutate({ dry: false });
  }

  // -- UI helpers ------------------------------------------------------------

  function toggleLines(dealId: string) {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  }

  function jumpTo(state: WorkbenchState) {
    if (state === "no_invoice" && !showNoInvoice) setShowNoInvoice(true);
    requestAnimationFrame(() => {
      document.getElementById(`workbench-${state}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  if (isLoading) return <p className="lede">Loading the reconciliation queue…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Queue unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  const total = rows.length;
  const dismissedCount = data.dismissed.length;

  return (
    <>
      <h1>Reconciliation workbench</h1>

      <Panel>
        <p className="lede" style={{ margin: 0 }}>
          {total === 0 ? (
            <strong>Queue clear — every closed-won deal has a commission or a dismissal.</strong>
          ) : (
            <>
              <strong>
                {total} deal{total === 1 ? "" : "s"} to resolve
              </strong>{" "}
              <span className="muted">— every closed-won deal without an active commission, in one place.</span>
            </>
          )}
        </p>
        {total > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
            {STATE_ORDER.map((state) => (
              <CountChip
                key={state}
                label={STATE_META[state].chip}
                count={data.counts[state]}
                onClick={() => jumpTo(state)}
              />
            ))}
          </div>
        )}
      </Panel>

      {total === 0 && (
        <Panel padding="tight">
          <EmptyState
            icon="deals"
            title="Nothing to reconcile"
            body="New closed-won deals land here automatically when they're missing a commission."
          />
        </Panel>
      )}

      {(["ready", "needs_review", "needs_link", "blocked"] as const).map((state) =>
        grouped[state].length === 0 ? null : (
          <Panel key={state} padding="tight" className="workbench-group">
            <div id={`workbench-${state}`} style={{ padding: "12px 14px 4px", scrollMarginTop: 72 }}>
              <h2 style={{ margin: 0, display: "flex", alignItems: "baseline", gap: 8 }}>
                {STATE_META[state].title}
                <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
                  · {grouped[state].length}
                </span>
              </h2>
              <p className="muted" style={{ margin: "4px 0 8px", fontSize: 12 }}>
                {STATE_META[state].blurb}
              </p>
            </div>
            <GroupTable
              state={state}
              rows={grouped[state]}
              expandedLines={expandedLines}
              onToggleLines={toggleLines}
              repOptions={repOptions}
              repsLoading={repsQuery.isLoading}
              repChoice={repChoice}
              onRepChoice={(dealId, userId) => setRepChoice((prev) => ({ ...prev, [dealId]: userId }))}
              busy={{
                trigger: trigger.isPending,
                dismiss: dismiss.isPending,
                mapOwner: mapOwner.isPending,
                link: linkAndCreate.isPending,
                classify: classify.isPending,
              }}
              onTrigger={onTrigger}
              onDismiss={onDismiss}
              onLinkAndCreate={onLinkAndCreate}
              onMapOwner={(row) => {
                const userId = repChoice[row.deal.id];
                if (!row.deal.hubspot_owner_id || !userId) return;
                mapOwner.mutate({ hubspotOwnerId: row.deal.hubspot_owner_id, userId });
              }}
              onClassify={(productId, action) => classify.mutate({ productId, action })}
            />
          </Panel>
        ),
      )}

      {data.counts.no_invoice > 0 && (
        <Panel padding="tight" className="workbench-group">
          <div id="workbench-no_invoice" style={{ padding: "12px 14px", scrollMarginTop: 72 }}>
            <button type="button" onClick={() => setShowNoInvoice((v) => !v)} style={{ fontSize: 12 }}>
              {showNoInvoice ? "Hide" : "Show"} {data.counts.no_invoice} deal{data.counts.no_invoice === 1 ? "" : "s"} with
              no invoice yet
            </button>
            {showNoInvoice && (
              <p className="muted" style={{ margin: "8px 0 0", fontSize: 12 }}>
                {STATE_META.no_invoice.blurb}
              </p>
            )}
          </div>
          {showNoInvoice && (
            <GroupTable
              state="no_invoice"
              rows={grouped.no_invoice}
              expandedLines={expandedLines}
              onToggleLines={toggleLines}
              repOptions={repOptions}
              repsLoading={repsQuery.isLoading}
              repChoice={repChoice}
              onRepChoice={() => {}}
              busy={{ trigger: false, dismiss: dismiss.isPending, mapOwner: false, link: false, classify: false }}
              onTrigger={onTrigger}
              onDismiss={onDismiss}
              onLinkAndCreate={onLinkAndCreate}
              onMapOwner={() => {}}
              onClassify={() => {}}
            />
          )}
        </Panel>
      )}

      <Panel padding="tight">
        <div style={{ padding: "12px 14px" }}>
          <button type="button" onClick={() => setShowDismissed((v) => !v)} style={{ fontSize: 12 }}>
            {showDismissed ? "Hide" : "Show"} dismissed ({dismissedCount})
          </button>
        </div>
        {showDismissed &&
          (dismissedCount === 0 ? (
            <p className="muted" style={{ padding: "0 14px 14px", fontSize: 12 }}>
              No dismissed deals.
            </p>
          ) : (
            <DataTable dense>
              <table>
                <thead>
                  <tr>
                    <th>Deal</th>
                    <th>Reason</th>
                    <th>Dismissed</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.dismissed.map((d) => (
                    <tr key={d.deal.id}>
                      <td data-label="Deal">
                        {d.deal.deal_name ?? "—"}
                        {d.deal.company_name && (
                          <div className="muted" style={{ fontSize: 11 }}>
                            {d.deal.company_name}
                          </div>
                        )}
                      </td>
                      <td data-label="Reason">{d.reason ?? "—"}</td>
                      <td data-label="Dismissed" className="muted">
                        {formatDate(d.at)}
                      </td>
                      <td data-label="">
                        <button
                          type="button"
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          disabled={restore.isPending}
                          onClick={() =>
                            restore.mutate({ dealId: d.deal.id, dealName: d.deal.deal_name ?? d.deal.hubspot_deal_id })
                          }
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTable>
          ))}
      </Panel>

      {/* Bulk assist: the legacy multi-strategy auto-matcher, collapsed. */}
      <Panel padding="tight">
        <div style={{ padding: "12px 14px" }}>
          <button type="button" onClick={() => setShowBulkAssist((v) => !v)} style={{ fontSize: 12 }}>
            {showBulkAssist ? "Hide" : "Show"} bulk assist (auto-matcher)
          </button>
        </div>
        {showBulkAssist && (
          <div style={{ padding: "0 14px 14px" }}>
            <p className="muted" style={{ marginTop: 0 }}>
              Matches unlinked Stripe invoices to HubSpot deals using (in order): metadata, customer email, email
              domain, company name, deal name. Only auto-links when exactly one deal matches, or when multiple match
              but only one closed-won within ±90 days of the invoice date. Anything else shows up below as ambiguous
              and stays unlinked until you act manually on /admin/invoices.
            </p>
            <div className="inline" style={{ gap: 12, marginTop: 12 }}>
              <button onClick={() => autoLink.mutate({ dry: true })} disabled={autoLink.isPending}>
                {autoLink.isPending ? "Running…" : "Dry-run"}
              </button>
              <button className="primary" onClick={onAutoLinkLive} disabled={autoLink.isPending}>
                {autoLink.isPending ? "Running…" : "Auto-link"}
              </button>
            </div>

            {lastRun && (
              <>
                <div className="grid cols-4" style={{ marginTop: 14 }}>
                  <Stat label="Scanned" value={lastRun.scanned} />
                  <Stat label="Linked" value={lastRun.linked} tone={lastRun.linked > 0 ? "ok" : "default"} />
                  <Stat
                    label="Ambiguous"
                    value={lastRun.ambiguous.length}
                    tone={lastRun.ambiguous.length > 0 ? "warn" : "default"}
                  />
                  <Stat label="Commissions created" value={lastRun.commissionsCreated} />
                </div>

                {lastRun.ambiguous.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <h3 style={{ margin: "0 0 8px", fontSize: 13 }}>Ambiguous matches (not auto-linked)</h3>
                    <DataTable dense>
                      <table>
                        <thead>
                          <tr>
                            <th>Stripe invoice</th>
                            <th>Customer</th>
                            <th>Candidates</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastRun.ambiguous.map((a) => (
                            <tr key={a.invoiceId}>
                              <td>
                                <code style={{ fontSize: 11 }}>{a.stripeInvoiceId}</code>
                              </td>
                              <td>
                                {a.customerName ?? a.customerEmail ?? "—"}
                                {a.customerEmail && a.customerName && (
                                  <div className="muted" style={{ fontSize: 11 }}>
                                    {a.customerEmail}
                                  </div>
                                )}
                              </td>
                              <td>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                  {a.candidates.map((cand) => (
                                    <li key={`${cand.dealId}:${cand.strategy}`} style={{ fontSize: 12 }}>
                                      <code style={{ fontSize: 11 }}>{cand.dealId.slice(0, 8)}</code>{" "}
                                      {cand.dealName ?? "—"} <span className="muted">· {cand.strategy}</span>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </DataTable>
                  </div>
                )}

                {lastRun.errors.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <h3 style={{ margin: "0 0 8px", fontSize: 13 }}>Errors</h3>
                    <ul style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>
                      {lastRun.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------------------
// Group table — one compact table per state, with the state-specific cell and
// its single primary action inline.
// ---------------------------------------------------------------------------

interface GroupTableProps {
  state: WorkbenchState;
  rows: WorkbenchRow[];
  expandedLines: Set<string>;
  onToggleLines: (dealId: string) => void;
  repOptions: RepUser[];
  repsLoading: boolean;
  repChoice: Record<string, string>;
  onRepChoice: (dealId: string, userId: string) => void;
  busy: { trigger: boolean; dismiss: boolean; mapOwner: boolean; link: boolean; classify: boolean };
  onTrigger: (row: WorkbenchRow) => void;
  onDismiss: (row: WorkbenchRow) => void;
  onLinkAndCreate: (row: WorkbenchRow) => void;
  onMapOwner: (row: WorkbenchRow) => void;
  onClassify: (productId: string, action: "commissionable" | "excluded" | "onboarding") => void;
}

const STATE_COLUMN: Record<WorkbenchState, string> = {
  ready: "Trigger invoice",
  needs_review: "Invoice to review",
  needs_link: "Suggested customer",
  blocked: "Blocker",
  no_invoice: "Status",
};

function GroupTable(props: GroupTableProps) {
  const { state, rows } = props;
  return (
    <DataTable dense>
      <table>
        <thead>
          <tr>
            <th>Deal</th>
            <th>Closed won</th>
            <th>Amount</th>
            <th>{STATE_COLUMN[state]}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <GroupRow key={row.deal.id} row={row} {...props} />
          ))}
        </tbody>
      </table>
    </DataTable>
  );
}

function GroupRow(props: GroupTableProps & { row: WorkbenchRow }) {
  const { row, expandedLines, onToggleLines, repOptions, repsLoading, repChoice, onRepChoice, busy } = props;
  const { deal } = row;
  const expanded = row.state === "needs_review" && expandedLines.has(deal.id);
  const productIds = useMemo(
    () => [...new Set((row.lineItems ?? []).map((l) => l.product).filter((p): p is string => p != null))],
    [row.lineItems],
  );

  return (
    <>
      <tr>
        <td data-label="Deal">
          {deal.deal_name ?? "—"}
          <div className="muted" style={{ fontSize: 11 }}>
            {deal.company_name ?? <code style={{ fontSize: 11 }}>{deal.hubspot_deal_id}</code>}
          </div>
        </td>
        <td data-label="Closed won" className="muted">
          {formatDate(deal.closed_won_at)}
        </td>
        <td data-label="Amount">{formatMoney(deal.amount, deal.currency)}</td>
        <td data-label={STATE_COLUMN[row.state]}>
          <StateCell row={row} expanded={expanded} onToggleLines={() => onToggleLines(deal.id)} />
        </td>
        <td data-label="Actions">
          <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <PrimaryAction
              row={row}
              busy={busy}
              repOptions={repOptions}
              repsLoading={repsLoading}
              repChoice={repChoice}
              onRepChoice={onRepChoice}
              onTrigger={() => props.onTrigger(row)}
              onLinkAndCreate={() => props.onLinkAndCreate(row)}
              onMapOwner={() => props.onMapOwner(row)}
            />
            <button
              type="button"
              style={{ padding: "4px 10px", fontSize: 12 }}
              disabled={busy.dismiss}
              onClick={() => props.onDismiss(row)}
              title="No commission expected — remove from the queue with a reason"
            >
              Dismiss
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ background: "var(--paper-soft, transparent)" }}>
            <LineItems row={row} productIds={productIds} busy={busy.classify} onClassify={props.onClassify} />
          </td>
        </tr>
      )}
    </>
  );
}

function StateCell({ row, expanded, onToggleLines }: { row: WorkbenchRow; expanded: boolean; onToggleLines: () => void }) {
  const inv = row.invoice;
  switch (row.state) {
    case "ready":
      return inv ? <InvoiceSummary invoice={inv} /> : <span className="muted">—</span>;
    case "needs_review":
      return (
        <div>
          {inv ? <InvoiceSummary invoice={inv} /> : <span className="muted">—</span>}
          <div style={{ marginTop: 4, display: "inline-flex", gap: 10, alignItems: "center" }}>
            <button type="button" style={{ padding: "2px 8px", fontSize: 11 }} onClick={onToggleLines}>
              {expanded ? "Hide line items" : `Line items (${row.lineItems?.length ?? 0})`}
            </button>
            {inv && (
              <a
                href={`https://dashboard.stripe.com/invoices/${encodeURIComponent(inv.stripe_invoice_id)}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 11 }}
              >
                Open in Stripe ↗
              </a>
            )}
          </div>
        </div>
      );
    case "needs_link": {
      const s = row.suggestion;
      if (!s) return <span className="muted">—</span>;
      return (
        <div>
          {s.name ?? s.email ?? s.stripeCustomerId}
          <div className="muted" style={{ fontSize: 11 }}>
            {s.name && s.email ? `${s.email} · ` : ""}
            matched via {s.reason}
          </div>
        </div>
      );
    }
    case "blocked":
      return (
        <div>
          <span className="badge pending">{row.blocker ? BLOCKER_LABEL[row.blocker] : "blocked"}</span>
          {row.invoice && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              invoice waiting: <StripeId id={row.invoice.stripe_invoice_id} /> ·{" "}
              {formatMoney(row.invoice.amount, row.invoice.currency)}
            </div>
          )}
        </div>
      );
    case "no_invoice":
      return <span className="muted">no paid invoice, no suggestion</span>;
  }
}

function PrimaryAction({
  row,
  busy,
  repOptions,
  repsLoading,
  repChoice,
  onRepChoice,
  onTrigger,
  onLinkAndCreate,
  onMapOwner,
}: {
  row: WorkbenchRow;
  busy: { trigger: boolean; mapOwner: boolean; link: boolean };
  repOptions: RepUser[];
  repsLoading: boolean;
  repChoice: Record<string, string>;
  onRepChoice: (dealId: string, userId: string) => void;
  onTrigger: () => void;
  onLinkAndCreate: () => void;
  onMapOwner: () => void;
}) {
  switch (row.state) {
    case "ready":
      return (
        <button type="button" className="primary" style={{ padding: "4px 10px", fontSize: 12 }} disabled={busy.trigger} onClick={onTrigger}>
          Create commission
        </button>
      );
    case "needs_link":
      return (
        <button type="button" className="primary" style={{ padding: "4px 10px", fontSize: 12 }} disabled={busy.link} onClick={onLinkAndCreate}>
          Link &amp; create
        </button>
      );
    case "blocked": {
      if (row.blocker === "no_rep_mapped") {
        const noOwnerId = !row.deal.hubspot_owner_id;
        const choice = repChoice[row.deal.id] ?? "";
        return (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <select
              value={choice}
              onChange={(e) => onRepChoice(row.deal.id, e.target.value)}
              disabled={noOwnerId || repsLoading}
              style={{ fontSize: 12, padding: "3px 6px", maxWidth: 180 }}
              aria-label={`Map rep for ${row.deal.deal_name ?? row.deal.hubspot_deal_id}`}
            >
              <option value="">{repsLoading ? "Loading reps…" : "Choose rep…"}</option>
              {repOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name ?? u.email}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="primary"
              style={{ padding: "4px 10px", fontSize: 12 }}
              disabled={noOwnerId || !choice || busy.mapOwner}
              title={
                noOwnerId
                  ? "This deal has no HubSpot owner id — assign an owner in HubSpot first, then re-sync."
                  : "Maps this HubSpot owner to the chosen user and backfills all their deals"
              }
              onClick={onMapOwner}
            >
              Map rep
            </button>
          </span>
        );
      }
      if (row.blocker === "no_structure") {
        return row.deal.owner_id ? (
          <Link
            to="/admin/reps/$id/structures"
            params={{ id: row.deal.owner_id }}
            style={{ padding: "4px 10px", fontSize: 12, border: "1px solid var(--gray300)", borderRadius: "var(--radius)", color: "var(--ink)", textDecoration: "none" }}
          >
            Add structure →
          </Link>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>
            map the rep first
          </span>
        );
      }
      return (
        <span className="muted" style={{ fontSize: 12 }}>
          set the amount in HubSpot
        </span>
      );
    }
    default:
      return null;
  }
}

function LineItems({
  row,
  productIds,
  busy,
  onClassify,
}: {
  row: WorkbenchRow;
  productIds: string[];
  busy: boolean;
  onClassify: (productId: string, action: "commissionable" | "excluded" | "onboarding") => void;
}) {
  const items = row.lineItems ?? [];
  const currency = row.invoice?.currency ?? null;
  if (items.length === 0) {
    return (
      <p className="muted" style={{ margin: "8px 0", fontSize: 12 }}>
        No line items found on the invoice payload.
      </p>
    );
  }
  return (
    <div style={{ padding: "8px 0" }}>
      <table style={{ width: "100%", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Description</th>
            <th style={{ textAlign: "left" }}>Amount</th>
            <th style={{ textAlign: "left" }}>Product</th>
            <th style={{ textAlign: "left" }}>Rule</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l, i) => (
            <tr key={i}>
              <td>{l.description ?? "—"}</td>
              <td>{formatMoney(l.amount, currency)}</td>
              <td>{l.product ? <code style={{ fontSize: 11 }}>{l.product}</code> : "—"}</td>
              <td>
                {l.product ? (
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    <button
                      type="button"
                      className="primary"
                      style={{ padding: "3px 8px", fontSize: 11 }}
                      disabled={busy}
                      onClick={() => onClassify(l.product!, "commissionable")}
                    >
                      ✓ Commissionable
                    </button>
                    <button
                      type="button"
                      style={{ padding: "3px 8px", fontSize: 11 }}
                      disabled={busy}
                      onClick={() => onClassify(l.product!, "excluded")}
                    >
                      ✗ Not commissionable
                    </button>
                    <button
                      type="button"
                      style={{ padding: "3px 8px", fontSize: 11 }}
                      disabled={busy}
                      onClick={() => onClassify(l.product!, "onboarding")}
                      title="Tags the product as onboarding (informational — does not affect commission)"
                    >
                      ★ Onboarding
                    </button>
                  </span>
                ) : (
                  <span className="muted">no product id — set rules on /admin/products</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {productIds.length > 1 && (
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 11 }}>
          {productIds.length} distinct products on this invoice — each rule applies platform-wide and recomputes all
          invoices.
        </p>
      )}
    </div>
  );
}

function InvoiceSummary({ invoice }: { invoice: WorkbenchInvoice }) {
  return (
    <div>
      <StripeId id={invoice.stripe_invoice_id} />
      <div className="muted" style={{ fontSize: 11 }}>
        paid {formatDate(invoice.paid_at)} · {formatMoney(invoice.amount, invoice.currency)}
      </div>
    </div>
  );
}

function StripeId({ id }: { id: string }) {
  const short = id.length > 18 ? `${id.slice(0, 18)}…` : id;
  return (
    <code style={{ fontSize: 11 }} title={id}>
      {short}
    </code>
  );
}

function CountChip({ label, count, onClick }: { label: string; count: number; onClick: () => void }) {
  const active = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active}
      style={{
        padding: "4px 10px",
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        background: active ? "var(--primary50)" : "transparent",
        border: "1px solid",
        borderColor: active ? "var(--primary300)" : "var(--hairline)",
        color: active ? "var(--accent-deep)" : "var(--muted)",
        borderRadius: 999,
        cursor: active ? "pointer" : "default",
      }}
    >
      {count} {label}
    </button>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "default" | "ok" | "warn" }) {
  return (
    <div
      className="metric"
      style={{
        background: tone === "ok" ? "var(--accent-2-soft)" : tone === "warn" ? "var(--warn-soft)" : undefined,
      }}
    >
      <div className="label">{label}</div>
      <div
        className="value"
        style={{ color: tone === "ok" ? "var(--accent-2)" : tone === "warn" ? "var(--warn)" : undefined }}
      >
        {value}
      </div>
    </div>
  );
}
